import { useState, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, X, Package, Warehouse as WarehouseIcon } from "lucide-react";
import { getStockItems } from "@/services/stockDataService";
import { useFormatCurrency } from "@/hooks/use-format-currency";
interface WarehouseItemsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouse: { code: string; name: string } | null | undefined;
}

export function WarehouseItemsDrawer({ open, onOpenChange, warehouse }: WarehouseItemsDrawerProps) {
  const formatCurrency = useFormatCurrency();
  const [searchQuery, setSearchQuery] = useState("");

  // Get all stock items for this warehouse
  const warehouseItems = useMemo(() => {
    if (!warehouse) return [];
    const allItems = getStockItems();
    return allItems.filter((item) => item.warehouseCode === warehouse.code);
  }, [warehouse]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return warehouseItems;
    const query = searchQuery.toLowerCase();
    return warehouseItems.filter(
      (item) =>
        item.code.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.locationCode?.toLowerCase().includes(query)
    );
  }, [warehouseItems, searchQuery]);

  // Calculate totals
  const totalValue = useMemo(() => {
    return filteredItems.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [filteredItems]);

  const totalQuantity = useMemo(() => {
    return filteredItems.reduce((sum, item) => sum + (item.physicalQuantity || 0), 0);
  }, [filteredItems]);

  if (!warehouse) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-5xl overflow-y-auto p-6">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <WarehouseIcon className="h-5 w-5" />
            {warehouse.name} - All Items
          </SheetTitle>
          <SheetDescription>
            All stock items in this warehouse, regardless of location assignment
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4 px-1">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Total Items</div>
              <div className="text-2xl font-bold">{filteredItems.length}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Total Quantity</div>
              <div className="text-2xl font-bold">{totalQuantity.toLocaleString()}</div>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Total Value</div>
              <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by item code, name, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Items Table */}
          {filteredItems.length > 0 ? (
            <div className="rounded-lg border">
              <div className="max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="font-semibold">Item Code</TableHead>
                      <TableHead className="font-semibold">Item Name</TableHead>
                      <TableHead className="font-semibold text-right">Physical Qty</TableHead>
                      <TableHead className="font-semibold text-right">Allocated</TableHead>
                      <TableHead className="font-semibold text-right">Unallocated</TableHead>
                      <TableHead className="font-semibold text-right">Free Qty</TableHead>
                      <TableHead className="font-semibold text-right">Due In</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item, index) => {
                      const physicalQty = item.physicalQuantity || 0;
                      const allocated = item.allocatedQuantity || 0;
                      const unallocated = physicalQty - allocated;
                      const dueIn = item.quantityDueIn || 0;
                      const freeQty = unallocated + dueIn;

                      return (
                        <TableRow key={item.code} className={index % 2 === 0 ? "bg-muted/30" : ""}>
                          <TableCell className="font-mono font-medium">{item.code}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell className="text-right font-mono">{physicalQty.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono">{allocated.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono">{unallocated.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono">{freeQty.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono">{dueIn.toLocaleString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Items Found</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                {searchQuery
                  ? "No items match your search criteria. Try adjusting your search."
                  : "This warehouse doesn't have any stock items yet."}
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
